/**
 * CSV 出力ユーティリティ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CSV値エスケープとUTF-8 BOM付きCSVのブラウザダウンロードを提供する共通ユーティリティ
 * @graph-connects none
 */

/** UTF-8 BOM（Excel で日本語が文字化けしないように先頭へ付与する） */
/** @graph-connects none */
const UTF8_BOM = String.fromCharCode(0xfeff);

/**
 * CSV formula injection 対策。`=` / `+` / `-` / `@` / Tab(0x09) / CR(0x0d) で始まるセルは
 * Excel / Google Sheets で数式として実行されうるため、先頭にシングルクォートを付けて無害化する。
 * 母票・子チケット summary・assignee 等は Backlog の自由入力テキストが由来のため必須。
 * @see https://owasp.org/www-community/attacks/CSV_Injection
 * @graph-connects none
 */
function neutralizeCsvFormula(str: string): string {
  return /^[=+\-@\t\r]/.test(str) ? `'${str}` : str;
}

/**
 * CSV 1セル分の値をエスケープする。数式注入を無害化したうえで、
 * カンマ・ダブルクォート・改行を含む場合はクォートで囲む。
 * @graph-connects none
 */
export function escapeCsvValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const str = neutralizeCsvFormula(String(value));
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * 行（セル配列）の二次元配列を CSV 文字列へ変換する。
 * @graph-connects none
 */
export function toCsv(rows: (string | number | null | undefined)[][]): string {
  return rows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
}

/**
 * ダウンロードファイル名に付与する現在時刻のタイムスタンプ（YYYYMMDD_HHmmss, ローカル時刻）。
 * @graph-connects none
 */
export function fileTimestamp(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${String(now.getFullYear())}${p(now.getMonth() + 1)}${p(now.getDate())}` +
    `_${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`
  );
}

/**
 * UTF-8 BOM 付き CSV を Blob 化しブラウザダウンロードをトリガーする。
 * @graph-connects none
 */
export function downloadCsv(filename: string, csvContent: string): void {
  const blob = new Blob([UTF8_BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
